import React, { useRef, useCallback, useEffect, useMemo } from 'react'

import { ComponentStoreState } from '../../../stores/ComponentStoreState'
import { InsightsDashboardPoolState } from '@aytm/app/insights/dashboard/duckness/ducks/types'
import usePool from '@duckness/use-pool'
import InsightsDashboardPool from '@aytm/app/insights/dashboard/duckness/InsightsDashboardPool'
// import { EventDuck, EVENT_ACTIONS } from '@aytm/app/insights/dashboard/duckness/ducks/EventDuck'

import './useBlink.css'
import { poolDispatch } from '@aytm/app/insights/dashboard/duckness/poolDispatch'
import { makeColumnByUidBlinkEventAction } from '@aytm/app/insights/dashboard/duckness/ducks/EventDuck/EventDuck.actionCreators'
import type { IPool } from '@duckness/pool'

const CLASS = 'insights-blink'

const timeout = 1250

let timer: number = 0

type BlinkContextType = {
  blinkIndex?: ComponentStoreState['blinkIndex']
  blinkUid: ComponentStoreState['blinkUid']
  hiddenItems: ComponentStoreState['hiddenItems']
  register: (node: HTMLElement, blinkUid: string) => () => void
  // eslint-disable-next-line immutablejs/no-native-map-set
  targetsMapRef: React.MutableRefObject<Map<HTMLElement, string>>
  onCallbackRef: React.MutableRefObject<(index: number) => void>
  startBlinking: (blinkUid: string) => void
  /**
   * @deprecated
   */
  waitBlinking?: (uid: string, callback: (index: number) => void) => void
}

const BlinkContext = React.createContext<BlinkContextType>(null)
/**
 * @param {React.ReactNode} children
 * @returns {React.ReactNode}
 */
export const BlinkProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const blinkUid = usePool<
    IPool<InsightsDashboardPoolState>,
    ComponentStoreState['blinkUid'],
    InsightsDashboardPoolState
  >(InsightsDashboardPool, state => state.currentData?.blinkUid ?? null)

  const mounted = useRef(false)
  const onCallbackRef = useRef<(index: number) => void>(null)

  // eslint-disable-next-line immutablejs/no-native-map-set
  const targetsMapRef = useRef(new Map<HTMLElement, string>())
  const register = useCallback<BlinkContextType['register']>((node, blinkUid) => {
    targetsMapRef.current.set(node, blinkUid)
    return () => {
      targetsMapRef.current.delete(node)
    }
  }, [])

  const startBlinking = useCallback<BlinkContextType['startBlinking']>((blinkUid: string) => {
    poolDispatch(makeColumnByUidBlinkEventAction(blinkUid))
    timer && clearTimeout(timer)
    timer = window.setTimeout(() => {
      if (mounted.current) {
        poolDispatch(makeColumnByUidBlinkEventAction(null))
        timer = null
      }
    }, timeout)
  }, [])

  useEffect(() => {
    const nodes =
      blinkUid == null
        ? ([] as HTMLElement[])
        : Array.from(targetsMapRef.current.entries(), ([node, uid]) =>
            uid === blinkUid && node && document.contains(node) && node.classList ? node : null
          ).filter(Boolean)

    if (!nodes.length) {
      return void 0
    }

    // eslint-disable-next-line compat/compat
    const controller = new AbortController()
    for (const node of nodes) {
      if (node.classList.contains(CLASS)) {
        continue
      }
      node.classList.add(CLASS)
      node.addEventListener(
        'animationend',
        () => {
          node.classList.remove(CLASS)
        },
        { passive: true, signal: controller.signal }
      )
    }
    return () => {
      controller.abort()
      for (const node of nodes) {
        node.classList.remove(CLASS)
      }
    }
  }, [blinkUid])

  useEffect(() => {
    mounted.current = true
    return () => {
      clearTimeout(timer)
      mounted.current = false
    }
  }, [])

  const contextValue = useMemo(
    () =>
      ({
        // blinkIndex,
        blinkUid,
        startBlinking,
        register,
        onCallbackRef,
        targetsMapRef
      }) as BlinkContextType,
    [blinkUid, register, startBlinking]
  )

  return <BlinkContext.Provider value={contextValue}>{children}</BlinkContext.Provider>
}

const useBlinkProvider = () => {
  const context = React.useContext(BlinkContext)
  if (!context) {
    throw new Error('useBlinkProvider must be used within a BlinkProvider')
  }
  return context
}

export const useBlinkSubscription = (blinkUid: string) => {
  const { register } = useBlinkProvider()
  const elementRef = useRef<HTMLElement>(null)
  useEffect(() => (elementRef.current ? register(elementRef.current, blinkUid) : undefined), [blinkUid, register])
  return elementRef
}

export const useBlinkControls = () => {
  const { startBlinking } = useBlinkProvider()
  // const { blinkUid, onCallbackRef, startBlinking, waitBlinking } = useBlinkProvider()
  // useEffect(() => {
  //   if (blinkUid && items) {
  //     const index = items.filter(v => hiddenItems.includes(v.uid)).findIndex(v => v.uid === blinkUid)
  //     if (index >= 0) {
  //       const callback = onCallbackRef.current
  //       if (callback) {
  //         callback(index)
  //         onCallbackRef.current = null
  //       }
  //       startBlinking(index)
  //       poolDispatch(makeColumnByUidBlinkEventAction(null))
  //     }
  //   }
  // }, [blinkUid, items, onCallbackRef, startBlinking, hiddenItems])
  return { startBlinking }
}
